import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import {
  InvoiceService,
  CreateInvoiceDto,
  UpdateInvoiceDto,
  InvoiceQueryParams,
  InvoiceStatus,
} from '../services/invoice.service';

export const useInvoicesPage = (params: InvoiceQueryParams) => {
  return useQuery({
    queryKey: ['invoices', 'page', params],
    queryFn: () => InvoiceService.getInvoicesPage(params),
    placeholderData: keepPreviousData,
  });
};

export const useInvoiceSummary = () => {
  return useQuery({
    queryKey: ['invoices', 'summary'],
    queryFn: () => InvoiceService.getInvoiceSummary(),
  });
};

// One invoice with its lines; list rows do not carry them
export const useInvoice = (id: string | null) => {
  return useQuery({
    queryKey: ['invoices', 'detail', id],
    queryFn: () => InvoiceService.getInvoiceById(id as string),
    enabled: !!id,
  });
};

// Seller details a new invoice starts with
export const useInvoiceSeller = (enabled = true) => {
  return useQuery({
    queryKey: ['invoices', 'seller'],
    queryFn: () => InvoiceService.getSeller(),
    enabled,
  });
};

export const useHSNCodes = () => {
  return useQuery({
    queryKey: ['hsn-codes'],
    queryFn: () => InvoiceService.getHSNCodes(),
    staleTime: 5 * 60 * 1000,
  });
};

const useInvalidateInvoices = () => {
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries({ queryKey: ['invoices'] });
};

export const useCreateInvoice = () => {
  const invalidate = useInvalidateInvoices();
  return useMutation({
    mutationFn: (data: CreateInvoiceDto) => InvoiceService.createInvoice(data),
    onSuccess: invalidate,
  });
};

export const useUpdateInvoice = () => {
  const invalidate = useInvalidateInvoices();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateInvoiceDto }) => InvoiceService.updateInvoice(id, data),
    onSuccess: invalidate,
  });
};

export const useUpdateInvoiceStatus = () => {
  const invalidate = useInvalidateInvoices();
  return useMutation({
    mutationFn: ({ id, status }: { id: string; status: InvoiceStatus }) =>
      InvoiceService.updateInvoiceStatus(id, status),
    onSuccess: invalidate,
  });
};

export const useDeleteInvoice = () => {
  const invalidate = useInvalidateInvoices();
  return useMutation({
    mutationFn: (id: string) => InvoiceService.deleteInvoice(id),
    onSuccess: invalidate,
  });
};

export const useGenerateEInvoice = () => {
  const invalidate = useInvalidateInvoices();
  return useMutation({
    mutationFn: (id: string) => InvoiceService.generateEInvoice(id),
    onSuccess: invalidate,
  });
};

// ---- GST returns ----

export const useReportingGSTINs = () => {
  return useQuery({
    queryKey: ['gst-reports', 'gstins'],
    queryFn: () => InvoiceService.getReportingGSTINs(),
  });
};

export const useGSTR1Report = (params: { gstin: string; fromDate: string; toDate: string } | null) => {
  return useQuery({
    queryKey: ['gst-reports', 'gstr1', params],
    queryFn: () => InvoiceService.generateGSTR1Report(params!.gstin, params!.fromDate, params!.toDate),
    enabled: !!params,
    placeholderData: keepPreviousData,
  });
};

export const useGSTR3BReport = (params: { gstin: string; fromDate: string; toDate: string } | null) => {
  return useQuery({
    queryKey: ['gst-reports', 'gstr3b', params],
    queryFn: () => InvoiceService.generateGSTR3BReport(params!.gstin, params!.fromDate, params!.toDate),
    enabled: !!params,
    placeholderData: keepPreviousData,
  });
};
